import EmbankmentModal from "components/Embankment/EmbankmentModal";
import SimulationConfigModal from "components/Simulation/SimulationConfigModal";
import { useGameContext } from "GameContext";
import { useWebSocket } from "WebSocketContext";
import GridCanvas from "components/Map/GridCanvas";

import IconButton from "components/General/IconButton";
import ImageButton from "components/General/ImageButton";

import Legend from "components/InfoTabs/Legend";

import InfoPopout from "components/Wrappers/InfoPopout";


import {
  LAND_TYPE_STRINGS,
  PASTORAL_VALUES,
  CLEARINGCOSTS,
  WATER_LEVEL_MANAGEMENT_SCHEMES,
} from "utils/simulator";

import {
  getNatureReserveCost,
  getEmbankmentCost,
  getDisembankmentCost,
} from "utils/game_utils";

import React, { useEffect, useMemo, useState, useRef } from "react";

import { allRoles, actions, numToLandType } from "utils/game-information-constants";
// highlight color classnames for text.
import { HIGHLIGHTS, ActionTooltips } from "utils/ToolTipConst";

function ActionButton({
  action = "",
  onClick = () => null,
  selected = false,
  children,
}: {
  action?: string;
  onClick?: () => void;
  selected?: boolean;
  children?: React.ReactNode;
}) {
const mapActionImages = {
  "clearing": "/ActionIcons/clearing.png",
  "cut": "/ActionIcons/Cut-Reed.png",
  "grazing": "/ActionIcons/Graze.png",
  "private-hunt": "/ActionIcons/Hunt.png",
  "public-hunt": "/ActionIcons/Hunt.png",
};
type MapActionKey = keyof typeof mapActionImages;
  const imgSrc = mapActionImages[action as MapActionKey];
  const capitalizedAction = action.charAt(0).toUpperCase() + action.slice(1);
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center bg-black
        ${selected ? "bg-opacity-10" : "bg-opacity-0"} p-2 rounded`}
    >
      <div
        className="relative h-10 w-10 rounded"
        style={{
          border:
            action === "embank"
              ? "solid"
              : action === "disembank" || action === "create-nature-reserve"
              ? "dashed"
              : "transparent",
          borderColor: action === "create-nature-reserve" ? "#008080" : "black",
        }}
      >
        {children}
        {imgSrc && <img className="h-full w-full" src={imgSrc} />}
      </div>
      <label>{capitalizedAction}</label>
    </button>
  );
}


export default function Map({

  showDucksOnly=false,
  showAnimals = false,
  showFullMap = false,
  enableTools = true,
  addSimulationResult,
  tutorialWindows,
  toggleTutorialWindow,
  getTutorialWindowState,


  recenterScreen=false,
}: {
  showDucksOnly?: boolean;
  showAnimals?: boolean;
  showFullMap?: boolean;
  enableTools?: boolean;
  addSimulationResult?: (
    waterRegime: number,
    mapActions: any,
    muActions: any,
    faunaActions: any,
    gameState: any
  ) => void;
  tutorialWindows: Record<
    string,
    { open: boolean; firstOpened: boolean }
  >;
  toggleTutorialWindow: (key: string) => void;
  getTutorialWindowState: (key: string) => { open: boolean; firstOpened: boolean };

  recenterScreen?: boolean;
}) {
  const { socket } = useWebSocket();
  const {
    connectedIds,
    playerInfo,
    hostId,
    currentGameState,
    privateEstateIds,
    isAdmin,
    publicEstateIds,

    proposedGameMapActions,
    proposedGameMuActions,
    proposedGameFaunaActions,
    reservationMuActions,

    currentYear,

    selectedWmRegime,

    minDebt,
    selfRoleMoney,
  } = useGameContext();

  const [mapData, setMapData] = useState([]);

  // TODO Action type
  const [proposedMapActions, setProposedMapActions] = useState<
    Record<string, any>
  >({});
  const [roleDisplayMapActions, setRoleDisplayMapActions] = useState<
    Record<string, any>
  >({});

  const [proposedMuActions, setProposedMuActions] = useState<
    Record<string, any>
  >({});
  const [proposedFaunaActions, setProposedFaunaActions] = useState<
    Record<string, any>
  >({});

  // console.log(proposedGameMapActions);
  // const [combinedMapActions, setCombinedMapActions] = useState({
  //   private: {},
  //   public: {},
  // });
  const [combinedMuActions, setCombinedMuActions] = useState({
    private: {},
    public: {},
  });
  const [combinedFaunaActions, setCombinedFaunaActions] = useState({
    private: {},
    public: {},
  });

  // console.log(combinedMapActions);

  const [dikeWR, setDikeWR] = useState(-1);

  const [selectedTool, setSelectedTool] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentView, setCurrentView] = useState("land"); // topo, water
  const [currentSeason, setCurrentSeason] = useState(1); // topo, water

  const [openSimModal, setOpenSimModal] = useState(false);
  const [openEmbankmentModal, setOpenEmbankmentModal] = useState(false);

  const cellData = currentGameState?.cellData;
  const simData = currentGameState?.simData;

  useEffect(() => {
    setProposedMapActions(proposedGameMapActions[playerInfo?.estate]);
    setProposedMuActions(proposedGameMuActions[playerInfo?.estate]);
    setProposedFaunaActions(proposedGameFaunaActions[playerInfo?.estate]);

    // setCombinedMapActions(proposedGameMapActions);
    // TODO setRoleDisplayMapActions (mainly for the host)
    setCombinedMuActions(proposedGameMuActions);
    setCombinedFaunaActions(proposedGameFaunaActions);
  }, [currentYear]);

  const playerRoleActions = useMemo(() => {
    for (let i = 0; i < allRoles.length; i++) {
      if (
        allRoles[i].role === playerInfo.role &&
        allRoles[i].estate === playerInfo.estate
      ) {
        return allRoles[i].actions;
      }
    }

    return [];
  }, [playerInfo, allRoles]);

  function onMessage(event: { data: string }) {
    if (event.data) {
      const message = JSON.parse(event.data);

      if (message.kind === "UpdateMuActions") {
        const newMuActions = message.muActions;

        const newProposedMap: Record<string, any> = {};

        for (let i = 0; i < newMuActions.length; i++) {
          const obj = newMuActions[i];
          newProposedMap[`${obj.managementUnit}`] = obj;
        }
        setProposedMuActions(newProposedMap);

        setCombinedMuActions((prev) => {
          return {
            ...prev,
            [`${message.estate}`]: newProposedMap,
          };
        });
        return;
      }

      if (message.kind === "UpdateRoleMapActions") {
        if (message.role === playerInfo?.role && message.estate === playerInfo?.estate) {
          const newMapActions = message.mapActions;
          const newProposedMap: Record<string, any> = {};
          for (let i = 0; i < newMapActions.length; i++) {
            const obj = newMapActions[i];
            newProposedMap[`${obj.location.x}-${obj.location.y}`] = obj;
          }
          setRoleDisplayMapActions(newProposedMap);
        }
      }

      /*
      if (message.kind === "UpdateMapActions") {
        const newMapActions = message.mapActions;
        const newProposedMap = {};
        for (let i = 0; i < newMapActions.length; i++) {
          const obj = newMapActions[i];
          newProposedMap[`${obj.location.x}-${obj.location.y}`] = obj;
        }

        setProposedMapActions(newProposedMap);
        setCombinedMapActions((prev) => {
          return {
            ...prev,
            [`${message.estate}`]: newProposedMap,
          };
        });

        return;
      }
      */

      if (message.kind === "UpdateFaunaActions") {
        const newMapActions = message.faunaActions;
        const newProposedMap: Record<string, any> = {};
        for (let i = 0; i < newMapActions.length; i++) {
          const obj = newMapActions[i];
          newProposedMap[`${obj.location.x}-${obj.location.y}`] = obj;
        }

        setProposedFaunaActions(newProposedMap);
        setCombinedFaunaActions((prev) => {
          return {
            ...prev,
            [`${message.estate}`]: newProposedMap,
          };
        });
      }
    }
  }

  // const intervall = useRef();

  useEffect(() => {
    if (!socket) return;
    socket.addEventListener("message", onMessage);

    // intervall.current = setInterval(() => {
    //   if (socket.readyState === WebSocket.OPEN) {
    //     socket.send(JSON.stringify({k: 'keep-alive'}));
    //   }
    // }, 30000);

    return () => {
      // clearInterval(intervall.current)
      if (socket) {
        socket.removeEventListener("message", onMessage);
      }
    };
  }, [socket]);

  const cellKey = (cell: any) => `${cell.x}-${cell.y}`;

  function getValidAction(cell: any, action: string) {
    if (actions[action].kind === "muAction") {
      return;
    }

    if (action === "clearing" && CLEARINGCOSTS[cell.landType] < 0) {
      return;
    }

    if (selfRoleMoney < minDebt && action === "clearing") {
      alert(`Your projected revenue reached the debt limit of ${minDebt}`);
      return
    }

    const landType = numToLandType(cell.landType);
    if (action === "cut" && !(landType.name === "reedbed" || landType.name === "reed water")) {
      return
    }

    if (
      action === "grazing" &&
      PASTORAL_VALUES[cell.landType][0] === 0 &&
      PASTORAL_VALUES[cell.landType][1] === 0 &&
      PASTORAL_VALUES[cell.landType][2] === 0 &&
      PASTORAL_VALUES[cell.landType][3] === 0
    ) {
      return;
    }


    return {
      authorId: playerInfo.id,
      authorRole: playerInfo.role,
      authorEstateClass: playerInfo.estate,
      actionKind: action,
      location: {
        x: cell.x,
        y: cell.y,
      },
    };
  }

  function startSimulation(waterRegime: number) {
    addSimulationResult &&
      addSimulationResult(
        waterRegime,
        currentGameState,
        roleDisplayMapActions,
        proposedMuActions,
        proposedFaunaActions
      );
  }

  function getSendToIds() {
    return connectedIds
  }

  const sendUpdateMapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  function sendUpdatedMapActions(mapActionProposal: Record<string, any>) {
    if (sendUpdateMapTimerRef.current) {
      clearTimeout(sendUpdateMapTimerRef.current);
    }

    sendUpdateMapTimerRef.current = setTimeout(() => {
      const sendToIds = getSendToIds();

      const arrayFromMaps = Object.values(mapActionProposal);
      const mes = JSON.stringify({
        // kind: "UpdateMapActions",
        kind: "UpdateRoleMapActions",
        mapActions: arrayFromMaps,
        toIds: sendToIds,
        estate: playerInfo.estate,
        role: playerInfo.role,
      });
      socket?.send(mes);
    }, 100)
  }

  function embankMU(
    managementUnit: number,
    wmR: number,
    isEmbank: boolean = true
  ) {
    console.log("Attempting embankment", managementUnit, wmR);
    const newProposedMuActions = { ...proposedMuActions };
    if (newProposedMuActions[`${managementUnit}`] && !isEmbank) {
      delete newProposedMuActions[`${managementUnit}`];
    } else {
      newProposedMuActions[`${managementUnit}`] = {
        authorId: playerInfo.id,
        authorRole: playerInfo.role,
        authorEstateClass: playerInfo.estate,
        actionKind: isEmbank ? "embank" : "disembank",
        managementUnit: managementUnit,
        wmRegime: WATER_LEVEL_MANAGEMENT_SCHEMES[wmR - 1],
      };
    }

    setProposedMuActions(newProposedMuActions);
    sendUpdatedMuActions(newProposedMuActions);
    setDikeWR(-1);
  }

  function rembankMU(
    managementUnit: number,
    wmR: number,
  ) {
    const sendToIds = getSendToIds();

    const mes = JSON.stringify({
      kind: "UpdateDikeMU",
      
      toIds: sendToIds,
      estate: playerInfo.estate,


      authorId: playerInfo.id,
      authorRole: playerInfo.role,
      authorEstateClass: playerInfo.estate,
      managementUnit: managementUnit,
      wmRegime: wmR,
    });
    socket?.send(mes);
    setDikeWR(-1);
  }

  const sendUpdateMuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  function sendUpdatedMuActions(muActions: Record<string, any>) {
    if (sendUpdateMuTimerRef.current) {
      clearTimeout(sendUpdateMuTimerRef.current);
    }
    sendUpdateMuTimerRef.current = setTimeout(() => {
      const sendToIds = getSendToIds();

      const arrayFromMaps = Object.values(muActions);
      const mes = JSON.stringify({
        kind: "UpdateMuActions",
        muActions: arrayFromMaps,
        toIds: sendToIds,
        estate: playerInfo.estate,
      });
      socket?.send(mes);
    }, 100)
  }

  const sendUpdateFaunaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  function sendUpdatedFaunaActions(faunaActions: Record<string, any>) {
    if (sendUpdateFaunaTimerRef.current) {
      clearTimeout(sendUpdateFaunaTimerRef.current);
    }
    sendUpdateFaunaTimerRef.current = setTimeout(() => {
      const sendToIds = getSendToIds();

      const arrayFromMaps = Object.values(faunaActions);
      const mes = JSON.stringify({
        kind: "UpdateFaunaActions",
        faunaActions: arrayFromMaps,
        toIds: sendToIds,
        estate: playerInfo.estate,
      });
      socket?.send(mes);
    }, 100)
  }

  const sendUpdateReservationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  function sendUpdatedReservationActions(managementUnit: number) {
    // CHECK GAMESTATE FOR EXISTING MU RESERVATION
    if (currentGameState.muData[managementUnit]?.natureReserve) {
      return;
    }

    const newReservationActions = { ...reservationMuActions };
    if (newReservationActions[managementUnit]) {
      delete newReservationActions[managementUnit];
    } else {
      newReservationActions[managementUnit] = {
        authorId: playerInfo.id,
        authorRole: playerInfo.role,
        authorEstateClass: playerInfo.estate,
        actionKind: "create-nature-reserve",
        managementUnit: managementUnit,
      };
    }

    if (sendUpdateReservationTimerRef.current) {
      clearTimeout(sendUpdateReservationTimerRef.current);
    }
    sendUpdateReservationTimerRef.current = setTimeout(() => {
      const sendToIds = getSendToIds();

      const mes = JSON.stringify({
        kind: "UpdateReservationMuActions",
        reservationMuActions: newReservationActions,
        toIds: sendToIds,
      });
      socket?.send(mes);
    }, 100);
  }

  if (!cellData) {
    return <div>Error: no data</div>;
  }

  return (
    <div className="h-[100%] w-[100%] flex flex-col">
      <div className="grow relative flex">
        <GridCanvas
          showAdmin={showFullMap && isAdmin}
          showDucksOnly={showDucksOnly}
          showAnimals={showAnimals}
          cols={showFullMap ? 10 : 5}
          selectedTool={selectedTool}
          showRight={playerInfo?.estate === "public"}
          gameState={currentGameState}
          currentView={currentView}
          currentYear={currentYear}
          currentWMRegime={ selectedWmRegime }
          currentSeason={currentSeason}
          onTileClick={(x: number, y: number) => {
            if (!enableTools) {
              return;
            }

            const cell = cellData[x][y];


            if (selectedTool === "create-nature-reserve") {
              const natureReserveCost = getNatureReserveCost(
                cell.managementUnit,
                currentGameState
              );
              if (natureReserveCost < 0) {
                return;
              }
              if (selfRoleMoney - natureReserveCost < minDebt) {
                alert(`Your projected revenue reached the debt limit of ${minDebt}`);
                return
              }

              sendUpdatedReservationActions(cell.managementUnit);
              // console.log(currentGameState.muData[cell.managementUnit]);
              // reservationMuActions[managementUnit]
              return;
            }

            const muEmCosts = getEmbankmentCost(
              cell.managementUnit,
              currentGameState
            );
            const muDiCosts = getDisembankmentCost(
              cell.managementUnit,
              currentGameState
            );

            switch (selectedTool) {
              case "embank":
                if (muEmCosts < 0) {
                  return;
                }

                if (selfRoleMoney - muEmCosts < minDebt) {
                  alert(`Your projected revenue reached the debt limit of ${minDebt}`);
                  return
                }

                if (currentGameState.muData[cell.managementUnit].embanked) {
                  // console.log("Already embanked");
                  rembankMU(cell.managementUnit, dikeWR);
                  return;
                }

                setIsDeleting(false);
                setSelectedTool("");
                embankMU(cell.managementUnit, dikeWR);
                return;
              case "disembank":
                if (muDiCosts < 0) {
                  return;
                }

                if (selfRoleMoney - muEmCosts < minDebt) {
                  alert(`Your projected revenue reached the debt limit of ${minDebt}`);
                  return
                }

                if (!currentGameState.muData[cell.managementUnit].embanked) {
                  if (
                    proposedMuActions[`${cell.managementUnit}`] === undefined
                  ) {
                    return;
                  }
                }

                setSelectedTool("");
                embankMU(cell.managementUnit, dikeWR, false);
                setIsDeleting(false);
                return;
            }

            if (
              roleDisplayMapActions[cellKey(cell)]?.actionKind === selectedTool
            ) {
              setIsDeleting(true);
            } else {
              if (
                selectedTool === "public-hunt" ||
                selectedTool === "private-hunt"
              ) {
                if (proposedFaunaActions[cellKey(cell)]) {
                  setIsDeleting(true);
                  return;
                }

                setIsDeleting(false);
              } else {
                setIsDeleting(false);
              }
            }
          }}
          proposedMapActions={
            showFullMap && isAdmin
              ? {
                  ...proposedGameMapActions["private"],
                  ...proposedGameMapActions["public"],
                }
              : roleDisplayMapActions
          }
          reservationActions={reservationMuActions}
          proposedMuActions={
            showFullMap && isAdmin
              ? {
                  ...combinedMuActions["private"],
                  ...combinedMuActions["public"],
                }
              : proposedMuActions
          }
          proposedFaunaActions={
            showFullMap && isAdmin
              ? {
                  ...combinedFaunaActions["private"],
                  ...combinedFaunaActions["public"],
                }
              : playerInfo?.role === "Hunter"
              ? proposedFaunaActions
              : {}
          }
          onSelectedTiles={(event) => {
            if (selectedTool === "") return;
            if (selectedTool === "embank" || selectedTool === "disembank")
              return;

            if (
              selectedTool === "private-hunt" ||
              selectedTool === "public-hunt"
            ) {
              const newProposedFaunaActions = { ...proposedFaunaActions };

              for (let i = 0; i < event.length; i++) {
                const tile = event[i];
                const cell = cellData[tile.x][tile.y];

                if (isDeleting) {
                  delete newProposedFaunaActions[`${cell.x}-${cell.y}`];
                } else {
                  const faunaAction = {
                    faunaName: "DUCK",
                    location: {
                      x: cell.x,
                      y: cell.y,
                    },
                    playerId: playerInfo.id,
                    playerEstate: playerInfo?.estate,
                    playerRole: playerInfo?.role,
                  };
                  newProposedFaunaActions[`${cell.x}-${cell.y}`] = faunaAction;
                }
              }

              setProposedFaunaActions(newProposedFaunaActions);
              sendUpdatedFaunaActions(newProposedFaunaActions);
              return;
            }

            const newProposedActions = { ...roleDisplayMapActions };

            for (let i = 0; i < event.length; i++) {
              const tile = event[i];
              const cell = cellData[tile.x][tile.y];

              if (isDeleting) {
                if (
                  newProposedActions[cellKey(cell)]?.actionKind === selectedTool
                ) {
                  delete newProposedActions[cellKey(cell)];
                }
              } else {
                const newAc = getValidAction(cell, selectedTool);
                if (newAc) {
                  newProposedActions[cellKey(cell)] = newAc;
                }
              }
            }
            // setProposedMapActions(newProposedActions);
            setRoleDisplayMapActions(newProposedActions);
            sendUpdatedMapActions(newProposedActions);

          }}
          recenterScreen={recenterScreen}
        />
      </div>

      <div className="absolute bottom-0 w-full flex flex-col bg-[#BCB7A8] z-[99]">
        <Legend mapMode={currentView} showBirds={playerInfo.role == "Conservationist"}/>
        <div className="py-2 w-full flex flex-row justify-between px-2 items-center overflow-x-auto">
          <div className="flex flex-row gap-1">
            {enableTools &&
              playerRoleActions.map((action, i) => (
                <div key={i} className="flex flex-col items-center relative">
                  <button
                    className={`
                      absolute -top-2
                      w-4 h-4 flex items-center justify-center
                      rounded-full bg-gray-300 text-black text-sm font-bold
                      hover:bg-gray-400 focus:outline-none
                      pointer-events-auto
                    `}
                    onClick={() => toggleTutorialWindow(action)}
                  >
                    ?
                  </button>
                  <ActionButton
                    action={action}
                    key={i}
                    onClick={() => {
                      console.log("Action button clicked");
                      // if the tutorial window for this action has not been opened, we want to open it.
                      if (getTutorialWindowState(action).firstOpened == false) {
                        toggleTutorialWindow(action);
                      }
                      if (selectedTool === action) {
                        setSelectedTool("");
                        return;
                      }
                      setSelectedTool(action);
                      switch (action) {
                        case "embank":
                          setOpenEmbankmentModal(true);
                          break;
                        case "disembank":
                          break;
                      }
                    }}
                    selected={action === selectedTool}
                  >
                    <InfoPopout
                      maxWidth={400}
                      popupContent={
                        ActionTooltips[action] ?? (
                          <p>Click to use this action.</p>
                        )
                      }
                    />
                  </ActionButton>
                </div>
              ))}
          </div>

          <div className="rounded-full border-black flex flex-row gap-2">
            <ImageButton
              imgSrc={"/ActionIcons/Land-Type_Biom.png"}
              text={""}
              onClick={() => setCurrentView("land")}
              selected={currentView === "land"}
            >
              <InfoPopout
                maxWidth={400}
                popupContent={
                  <div>
                    <p>Biome View</p>
                  </div>
                }
              />
            </ImageButton>

            <ImageButton
              imgSrc={"/ActionIcons/Topology.png"}
              text={""}
              onClick={() => setCurrentView("topo")}
              selected={currentView === "topo"}
            >
              <InfoPopout
                maxWidth={400}
                popupContent={
                  <div>
                    <p>Topological View</p>
                  </div>
                }
              />
            </ImageButton>

            {currentYear > 1 && (
              <ImageButton
                imgSrc={"/ActionIcons/Water-Level.png"}
                text={""}
                onClick={() => setCurrentView("water")}
                selected={currentView === "water"}
              >
                <InfoPopout
                  maxWidth={400}
                  popupContent={
                    <div>
                      <p>Water View</p>
                    </div>
                  }
                />
              </ImageButton>
            )}
            {currentView === "water" && (
              <div className="text-xs items-center flex flex-row gap-2 justify-center">
                <IconButton
                  icon={"Sp"}
                  onClick={() => setCurrentSeason(1)}
                  selected={currentSeason === 1}
                />
                <IconButton
                  icon={"Su"}
                  onClick={() => setCurrentSeason(2)}
                  selected={currentSeason === 2}
                />
                <IconButton
                  icon={"F"}
                  onClick={() => setCurrentSeason(3)}
                  selected={currentSeason === 3}
                />
                <IconButton
                  icon={"W"}
                  onClick={() => setCurrentSeason(4)}
                  selected={currentSeason === 4}
                />
              </div>
            )}
          </div>

          <div className="flex flex-row justify-end">
            {enableTools && (
              <ImageButton
                imgSrc={"/ActionIcons/Run-Simulation.png"}
                text={"Run Simulation"}
                onClick={() => setOpenSimModal(true)}
              />
            )}
          </div>
        </div>
      </div>

      <SimulationConfigModal
        open={openSimModal}
        setOpen={setOpenSimModal}
        cellData={cellData}
        gameState={currentGameState}
        mapActions={roleDisplayMapActions}
        muActions={proposedMuActions}
        faunaActions={proposedFaunaActions}
        startSim={startSimulation}
      />

      <EmbankmentModal
        open={openEmbankmentModal}
        clearTool={() => {
          setSelectedTool("");
        }}
        setOpen={setOpenEmbankmentModal}
        setEmbankWR={setDikeWR}
        wr={dikeWR}
      />
    </div>
  );
}
